import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCategoryLabels } from '@/features/common/useCategoryLabels';
import { useLanguageFallback } from '@/app/shells/PublicShell';
import { LANGUAGE_FALLBACK } from '@/features/common/languageFallback';
import { PUBLIC_RECORD, publicRecordState } from '@/features/publicsite/publicRecord';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicNews } from '@/i18n/dictionaries/publicNews';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { RelatedStories } from '@/features/publicsite/RelatedStories';
import { publicContentApi } from '@/features/publicsite/content/api/publicContentApi';
import { STORY_COVER, storyCoverLayout } from '@/features/publicsite/content/storyCover';
import { STORY_TYPE } from '@/features/publicsite/news/storyType';
import type { ContentPageProps } from '@/features/publicsite/types/content/ContentPageProps';
import type { ResolvedContent } from '@/features/publicsite/types/content/ResolvedContent';
import { ContentStory } from '@/features/common/ContentStory/ContentStory';
import { newsCoverUrl } from '@/features/common/newsCoverUrl';
import { imageExists } from '@/features/common/imageExists';
import { Icon } from '@/features/common/Icon';
import { formatDate } from '@/features/common/formatDate';
// A page or a policy is the same .content-page block without the story's dress (see ContentStory).
import '@/features/common/ContentStory/content-page.css';

/**
 * One piece of administered content: a news item, an article, a CMS page, or a policy.
 *
 * The four routes render the same thing — a title and an authored body — and differed in the
 * server-rendered site only by which service method resolved the slug. Here they differ by which
 * endpoint the route hands in, which is the same distinction with less duplication: `/stiri/{slug}`
 * passes the news resolver (NEWS first, then ARTICLE, per ContentService's collision rule),
 * `/pagini/{slug}` the page one, and `/politici/{slug}` the policy one.
 *
 * ON THE RAW HTML. The body is inserted unescaped, which is what `th:utext` did in page.html and
 * what a CMS body has to do to be a CMS body. Worth stating rather than leaving implicit, because
 * `dangerouslySetInnerHTML` reads as alarming in review while `th:utext` hides inside an
 * attribute. What makes it safe is on the write path, where this paragraph used to say the fix
 * belonged: every body is stored through the backend's HtmlSanitizer, an allow-list (headings,
 * lists, links, images from the media store, tables — no script, no h1), so what arrives here is
 * already the sanitised text, for React and for every other consumer of the same row.
 *
 * A STORY (`story`, the /stiri route) is dressed as one: the way back to the list, the cover the
 * reader clicked, the category over the headline and the date under it. The date is the part that
 * was missing (T193) — the card on /stiri printed it and the story it opened did not, because the
 * story endpoint did not return it. Pages and policies are none of that: they have no date a
 * reader would look for, no category and no list to go back to.
 */
export function ContentPage({ endpoint, story = false }: ContentPageProps) {
  const { slug = '' } = useParams();
  const { t } = useTranslation(publicNews);
  const { language } = useLanguage();
  const setFallback = useLanguageFallback();
  const categoryName = useCategoryLabels();
  const [content, setContent] = useState<ResolvedContent | null>(null);
  // Whether the slug convention's picture exists (null: still asking), and whether the picture shown
  // failed to load — see storyCoverLayout. State rather than a direct style write, so a second story
  // with a real cover is not left hidden by the first one's failure.
  const [legacyCover, setLegacyCover] = useState<boolean | null>(null);
  const [coverFailed, setCoverFailed] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let live = true;
    setMissing(false);
    // Cleared here, not just on a 404: react-router keeps this component mounted across two
    // slugs of the same route (/stiri/a → /stiri/b), so without this the previous article's
    // title and body stayed on screen — and, since usePublicPageTitle reads `content` below,
    // the document title lagged the same way — for the whole time the new one was in flight.
    setContent(null);
    // For the same reason: a story whose cover file was missing left the flag up, and the next
    // story opened in the same mounted component then hid a cover that did exist.
    setCoverFailed(false);
    publicContentApi
      .resolve(endpoint, slug)
      .then((r) => {
        if (!live) return;
        setContent(r);
        setFallback(r.languageFallback ? LANGUAGE_FALLBACK.whole : LANGUAGE_FALLBACK.none);
      })
      .catch((e) => {
        if (!live) return;
        // A 404 is a real answer here — the slug does not exist, or exists unpublished. Anything
        // else (a transient rate limit, a network blip) has no dedicated UI of its own, but must
        // still resolve out of the "loading" state below rather than spin forever — reusing the
        // not-found screen is an approximation, not a lie: either way there is nothing to read.
        if (publicRecordState(e) !== PUBLIC_RECORD.missing) {
          console.error('Failed to load content', e);
        }
        setMissing(true);
      });
    return () => {
      live = false;
      // The notice belongs to this page; leaving it raised would carry it onto the next one.
      setFallback(LANGUAGE_FALLBACK.none);
    };
  }, [slug, language, endpoint, setFallback]);

  // Asked alongside the story, not after it: a static file answers before the API does, so the page
  // is not held back in practice — it is laid out once, with or without the picture's frame.
  useEffect(() => {
    if (!story) return;
    let live = true;
    setLegacyCover(null);
    void imageExists(newsCoverUrl({ slug })).then((exists) => {
      if (live) setLegacyCover(exists);
    });
    return () => {
      live = false;
    };
  }, [slug, story]);

  usePublicPageTitle(missing ? t('notFound') : (content?.title ?? null));

  if (missing) {
    // Back to where this kind of content lives: a missing story to the news list, a missing page
    // or policy to the home page. Every route used to offer "Înapoi la știri", which on a broken
    // /politici link sent the reader somewhere they had never been.
    return (
      <article className="container section content-page">
        <h1>{t('notFound')}</h1>
        <p>
          {story ? (
            <Link to="/stiri">{t('backToList')}</Link>
          ) : (
            <Link to="/">{t('backToHome')}</Link>
          )}
        </p>
      </article>
    );
  }

  if (!content) {
    return (
      <article className="container section content-page">
        <p className="empty-state">{t('loading')}</p>
      </article>
    );
  }

  // lang marks the text when its content language differs from the page's, so assistive technology
  // switches voice for what it is about to read (WCAG 3.1.2).
  const contentLang =
    content.contentLanguage.toLowerCase() !== language
      ? content.contentLanguage.toLowerCase()
      : undefined;
  const heading = <h1 lang={contentLang}>{content.title}</h1>;
  const body = (
    // HTML SINK — source: the stored body of a news item, article, page or policy · written by:
    // any authenticated user (ContentContributionController takes a body; publishing needs
    // approval) · sanitised by: the backend HtmlSanitizer, on the write path. See the note in this
    // module's header: a body has to be markup to be a body, and the allow-list is what makes it
    // safe, applied where it is stored rather than here.
    <div
      className="content-page__body"
      lang={contentLang}
      dangerouslySetInnerHTML={{ __html: content.body ?? '' }}
    />
  );

  if (!story) {
    return (
      <article className="container section content-page">
        {heading}
        {body}
      </article>
    );
  }

  const published = formatDate(content.publishedAt, language);
  // The category the card showed — and the card's own fallback when there is none, so the two agree.
  // In the page's language; RelatedStories below is still given the stored text to ask by.
  const category = content.category ? categoryName(content.category) : t('defaultCategory');
  const coverLayout = storyCoverLayout(
    Boolean(content.coverMediaAssetId),
    legacyCover,
    coverFailed,
  );
  // Still asking whether the guessed picture exists: a moment longer on "loading" rather than a
  // page that rearranges itself under the reader once the answer comes.
  if (coverLayout === STORY_COVER.pending) {
    return (
      <article className="container section content-page">
        <p className="empty-state">{t('loading')}</p>
      </article>
    );
  }

  // A story reads top to bottom in one column: the way back, the picture with its category on it
  // (as the card the reader clicked showed it), the headline, when, the text — then the related
  // stories. Every block shares the one 720px measure, centred, the picture and the related cards
  // included: at full container width the photo belonged to the page, and left-aligned it left a
  // gutter of nothing on the right of every line.
  //
  // The related stories are a sibling of the <article>, not inside it: they are not part of the
  // story, and .content-page's rules for the body (links, paragraphs) would restyle the cards.
  return (
    <div className="container section story-page">
      <div className="story-page__column">
        <ContentStory
          titleTag="h1"
          title={content.title}
          lang={contentLang}
          category={category}
          // No picture to carry it: the same chip, set in the flow over the title, so the category
          // is not lost with the image.
          coverSrc={coverLayout === STORY_COVER.chip ? null : newsCoverUrl(content)}
          onCoverError={() => setCoverFailed(true)}
          bodyHtml={content.body ?? ''}
          leading={
            // The same "back to the list" every public detail page leads with (.detail-back).
            <Link className="detail-back" to="/stiri">
              <Icon name="chevron-left" className="detail-back-icon" />
              {t('backToList')}
            </Link>
          }
          meta={
            published && (
              <>
                {/* <time> so the date is a date to a machine as well — a reader's calendar, a
                    crawler, an assistive technology that reads dates its own way. */}
                <time dateTime={content.publishedAt ?? undefined}>
                  {t('publishedOn', { date: published })}
                </time>
                {content.contentType === STORY_TYPE.article && <span>{t('typeArticle')}</span>}
              </>
            )
          }
        />
        <RelatedStories slug={content.slug} category={content.category} />
      </div>
    </div>
  );
}
